/**
 * The structural half of the anonymous-by-default posture (D-4).
 *
 * This module is pure and imports nothing, which is deliberate on two counts:
 * it is shared with the browser bundle by sub-project 3, and it must be
 * testable without a tracer provider.
 *
 * Why an allowlist and not a denylist of bad words: a denylist fails open. The
 * span that leaks is always the one nobody reviewed, which is exactly the span
 * a denylist has no entry for. This fails closed instead - an attribute nobody
 * thought about is dropped, and the cost of that is a missing attribute rather
 * than a leaked sentence.
 */

/**
 * Namespaces known to carry machine facts rather than learner content. Sentry
 * and the OpenTelemetry instrumentations own most of these; `convo.` is ours.
 */
export const ALLOWED_ATTRIBUTE_PREFIXES: readonly string[] = [
  "http.",
  "db.",
  "url.",
  "server.",
  "network.",
  "messaging.",
  "gen_ai.",
  "otel.",
  "sentry.",
  "convo.",
];

/**
 * Keys inside an allowed namespace that are unsafe anyway.
 *
 * EVERY ENTRY BELOW WAS OBSERVED ON A REAL SPAN, not imagined. The list was
 * rebuilt by booting `@sentry/node` with tracing on and dumping the attribute
 * keys its own integrations produced against an HTTP request, a Prisma query
 * and an OpenAI call. That matters because the allowlist fails closed against
 * keys nobody thought of and fails OPEN against keys somebody else's library
 * thought of first: `db.`, `http.` and `gen_ai.` are all allowed prefixes, so
 * anything Sentry's auto-instrumentation puts under them ships unless it is
 * named here. Re-run that enumeration before trusting this file again after a
 * Sentry upgrade.
 */
export const DENIED_ATTRIBUTE_KEYS: readonly string[] = [
  // Full URLs and query strings. `/api/words/lookup?word=<learner text>` is a
  // live route in this codebase, so the query string is learner content.
  "url.full",
  "url.query",
  "http.url",
  "http.target", // `${pathname}${search}` - the query string is in it
  "http.query",
  // The learner themselves.
  "http.client_ip", // first x-forwarded-for entry
  "http.user_agent",
  // Prisma's own instrumentation, which Sentry's bundled `prismaIntegration`
  // switches on. `db.connection_string` carries DATABASE_URL, password and all.
  "db.query.text",
  "db.statement",
  "db.connection_string",
  "db.user",
  // The gen_ai semantic conventions define prompt and completion capture on
  // both sides. Sentry's OpenAI/Anthropic integrations write the response side.
  "gen_ai.prompt",
  "gen_ai.completion",
  "gen_ai.input.messages",
  "gen_ai.output.messages",
  "gen_ai.response.text",
  "gen_ai.system_instructions",
  "gen_ai.embeddings.input",
  "gen_ai.tool.input",
  "gen_ai.tool.output",
];

/**
 * Denied by PREFIX rather than by exact name, for families whose members are
 * named after runtime data and so cannot be enumerated.
 *
 * Sentry's HTTP instrumentation flattens every request and response header into
 * `http.request.header.<name>` / `http.response.header.<name>`, including
 * `authorization` and one key per cookie (`http.request.header.cookie.session`
 * was observed). No exact-match list can cover a namespace whose keys come from
 * the wire.
 */
export const DENIED_ATTRIBUTE_PREFIXES: readonly string[] = [
  "http.request.header.",
  "http.response.header.",
];

/**
 * Learner text is long. Route labels, model names, capabilities and ids are
 * short. Over-length string values are DROPPED, not truncated: the first 256
 * characters of a learner's paragraph is still the learner's paragraph.
 */
export const MAX_ATTRIBUTE_STRING_LENGTH = 256;

function isAllowedKey(key: string): boolean {
  if (DENIED_ATTRIBUTE_KEYS.includes(key)) return false;
  if (DENIED_ATTRIBUTE_PREFIXES.some((prefix) => key.startsWith(prefix))) return false;
  return ALLOWED_ATTRIBUTE_PREFIXES.some((prefix) => key.startsWith(prefix));
}

function isAllowedValue(value: unknown): boolean {
  if (typeof value === "string") return value.length <= MAX_ATTRIBUTE_STRING_LENGTH;
  if (typeof value === "number" || typeof value === "boolean") return true;
  // Arrays and objects are not span attribute types in OpenTelemetry, and an
  // object is the shape a leak arrives in. Drop them.
  return false;
}

export function scrubAttributes(
  data: Record<string, unknown> | undefined,
): Record<string, unknown> {
  if (!data) return {};
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    if (!isAllowedKey(key)) continue;
    if (!isAllowedValue(value)) continue;
    out[key] = value;
  }
  return out;
}

/**
 * Sentry's `beforeSendSpan` hook. Mutates and returns the same object: Sentry
 * requires a span back, and a root span may not be dropped.
 *
 * SCOPE LIMIT, READ THIS BEFORE ENABLING AN INTEGRATION. This scrubs
 * `span.data` and NOTHING ELSE. The span NAME (`span.description`) is not
 * touched, and deliberately so: the name is the only thing that makes a span
 * findable, and there is no allowlist of safe names the way there is of safe
 * attribute keys. So the allowlist above does not protect names, and no entry
 * added to `DENIED_ATTRIBUTE_KEYS` ever will.
 *
 * This is not hypothetical. Sentry's `Postgres` and `PostgresJs` integrations
 * name their `db` span after the raw SQL statement, which put full SELECTs with
 * schema, table and column names into `span.description` while every allowlist
 * check passed. The fix had to be upstream of this file - both integrations are
 * now filtered out in `lib/sentry/config.ts`. An integration that names spans
 * after runtime data must be DISABLED there; it cannot be made safe here.
 *
 * Typed structurally rather than against Sentry's `SpanJSON` so this file stays
 * importable from the browser bundle without pulling a server type in.
 */
export function beforeSendSpan<T extends { data?: Record<string, unknown> }>(span: T): T {
  if (span.data) span.data = scrubAttributes(span.data);
  return span;
}
